import { makeStyles } from '@material-ui/styles'
import {
  getCinderQuotaUsage,
  getComputeQuotaUsage,
  listUserTenants,
} from 'app/plugins/account/components/userManagement/tenants/new-actions'
import { userTenantsSelector } from 'app/plugins/account/components/userManagement/tenants/selectors'
import ClusterHostGroupsPicklist from 'app/plugins/openstack/components/infrastructure/clusters/ClusterHostGroupsPicklist'
import SshKeyPicklist from 'app/plugins/openstack/components/vms/SshKeyPicklist'
import { useAppSelector } from 'app/store'
import SimpleLink from 'core/components/SimpleLink'
import useListAction from 'core/hooks/useListAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import Text from 'pf9-ui-components/built/elements/Text'
import React, { useEffect, useMemo, useState } from 'react'
import SelectableCardWrapper from './SelectableCardWrapper'
import TenantsTable from './TenantsTable'

interface TenantsInfo {
  name: string
  cores: { in_use: number; limit: number }
  ram: { in_use: number; limit: number }
  storage: { in_use: number; limit: number }
}

interface ClusterArchitectureFieldsProps {
  params: any
  getParamsUpdater: (key: string) => (value: unknown) => void
  setError: (error: { title: string; message: string }) => void
}

/**
 * Cluster Architecture Fields component
 * @param param0 - tenants, params, getParamsUpdater, loading
 * @returns Cluster Architecture Fields component is used for cluster architecture selection for both OpenStack and BYO infrastructure
 * Based on the selected infrastructure in ClusterInfrastructureSelector, the form will be rendered accordingly
 */
export default function ClusterArchitectureFields({
  params,
  getParamsUpdater,
  setError,
}: ClusterArchitectureFieldsProps) {
  const classes = useStyles()
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()
  const [tenantsInfo, setTenantsInfo] = useState<TenantsInfo[]>([])
  const [loadingTenantsInfo, setLoadingTenantsInfo] = useState(false)

  // Tenants
  const { loading: loadingTenants } = useListAction(listUserTenants)
  const tenants = useAppSelector(userTenantsSelector)
  const tenantName = useMemo(() => tenants?.find((tenant) => tenant?.id === currentTenant)?.name, [
    tenants,
    currentTenant,
  ])

  useEffect(() => {
    if (tenantName) {
      getParamsUpdater('tenant')(tenantName)
    }
  }, [tenantName])

  // Fetch compute quotas
  useEffect(() => {
    if (!currentTenant) {
      return
    }
    const fetchComputeQuotas = async () => {
      setLoadingTenantsInfo(true)
      try {
        const computeQuotas = await getComputeQuotaUsage({ tenantId: currentTenant })
        const cinderQuotas = await getCinderQuotaUsage({ tenantId: currentTenant })
        setTenantsInfo([
          {
            name: tenantName,
            cores: computeQuotas?.cores,
            ram: computeQuotas?.ram,
            storage: cinderQuotas?.gigabytes,
          },
        ])
      } catch (err) {
        setError({ title: 'Error fetching tenant quotas', message: err?.message })
      }
      setLoadingTenantsInfo(false)
    }
    fetchComputeQuotas()
  }, [currentTenant, tenantName])

  return (
    <>
      {/* Select OpenStack Infrastructure */}
      <div className={classes.clusterArchitectureFields}>
        <Text variant="body2">
          Select the compute infrastructure for this cluster. Using Virtualized Clusters enables
          integrated management of node pools, cluster autoscaler, storage and load balancers.
        </Text>
        <TenantsTable
          data={tenantsInfo}
          selectedTenantName={params?.tenant}
          onTenantSelection={getParamsUpdater('tenant')}
          loading={loadingTenants || loadingTenantsInfo}
        />
        <div className={classes.controlPlaneOptions}>
          <Text variant="body2">Select the type of control plane.</Text>
          <div className={classes.SelectableCardWrappers}>
            <SelectableCardWrapper
              id="managed"
              onClick={getParamsUpdater('controlPlaneType')}
              active={params?.controlPlaneType === 'managed'}
              title="Managed Control Plane"
              description="On PCD"
            />
            <SelectableCardWrapper
              id="custom"
              // Set the onClick to noop till option is supported
              onClick={() => {}}
              // onClick={() => getParamsUpdater('controlPlaneType')}
              active={params?.controlPlaneType === 'custom'}
              title="Custom Control Plane"
              description="Advanced"
              comingSoon
            />
          </div>
        </div>

        <div className={classes.dropdownFields}>
          {/* Select Availability Zone */}
          <DropdownField
            id="openstackCluster"
            label="Virtualized Cluster"
            DropdownComponent={ClusterHostGroupsPicklist}
            value={params?.openstackCluster}
            onChange={getParamsUpdater('openstackCluster')}
            fullWidth
            info="Select the virtualized cluster to which the kubernetes cluster will be attached."
            disableClusterWithoutHosts
            required
          />
          <div className={classes.sshKey}>
            <SimpleLink
              className={classes.createSshKey}
              src={routeManager.routes.openstack.importSshKey.path()}
            >
              Create SSH Key
            </SimpleLink>
            <DropdownField
              id="sshKey"
              label="SSH Key"
              DropdownComponent={SshKeyPicklist as React.FC<any>}
              value={params?.sshKey}
              onChange={getParamsUpdater('sshKey')}
              fullWidth
              info="Select an SSH key for your VMs."
              offset={{ vertical: 0, horizontal: -50 }}
              hideNoneOption
              required
            />
          </div>
        </div>

        {/* Conneting this out till Custom Control Plane is supported */}
        {/* <DropdownField
              id="controlPlaneClass"
              label="Control Plane Class"
              DropdownComponent={AsyncDropdown}
              items={controlPlaneClassOptions}
              value={params?.alertMethod}
              onChange={getParamsUpdater('controlPlaneClass')}
              disabled={params?.controlPlaneType === 'managed'}
              selectFirst
            /> */}
      </div>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  clusterArchitectureFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  controlPlaneOptions: {
    display: 'grid',
    margin: theme.spacing(2, 0),
    gridGap: theme.spacing(2),
  },
  SelectableCardWrappers: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gridGap: theme.spacing(2),
  },
  dropdownFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(2),
    alignItems: 'baseline',
  },
  sshKey: {
    position: 'relative',
  },
  createSshKey: {
    position: 'absolute',
    right: 0,
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    fontSize: '11px',
    zIndex: 100,
  },
}))
